/**
 * ApiDocsPage — Documentación pública de la API REST de Dealider.
 *
 * Página estática estilo Stripe / Twilio: sidebar de secciones a la izquierda,
 * contenido + bloques de código a la derecha. No requiere autenticación.
 * Accesible desde el footer de la Landing y desde el botón "Documentación REST"
 * dentro del API Hub.
 */
import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Copy, BookOpen, ArrowLeft, ChevronRight, Code, ShieldCheck, Zap, Database, Link2, MessageSquare } from 'lucide-react';
import { BRAND, APP_NAME } from '@/lib/brand';

// ─────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────

function CodeBlock({ code, lang = 'bash' }) {
  return (
    <div className="relative group">
      <pre className="bg-slate-950 border border-white/10 rounded-lg p-4 text-[12px] overflow-x-auto">
        <code className={`language-${lang} text-emerald-300 font-mono whitespace-pre`}>{code}</code>
      </pre>
      <button
        onClick={async () => {
          try { await navigator.clipboard.writeText(code); toast.success('Copiado'); }
          catch {
            try {
              const ta = document.createElement('textarea');
              ta.value = code; ta.style.position = 'fixed'; ta.style.opacity = '0';
              document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta);
              toast.success('Copiado');
            } catch { toast.info('No se pudo copiar automáticamente.'); }
          }
        }}
        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition bg-slate-800 hover:bg-slate-700 text-white text-[10px] px-2 py-1 rounded inline-flex items-center gap-1"
        data-testid="docs-copy-btn"
      >
        <Copy className="w-3 h-3" /> Copiar
      </button>
    </div>
  );
}

function EndpointBox({ method, path, desc }) {
  const colors = {
    GET: 'border-emerald-400/40 text-emerald-300 bg-emerald-500/5',
    POST: 'border-blue-400/40 text-blue-300 bg-blue-500/5',
    PUT: 'border-amber-400/40 text-amber-300 bg-amber-500/5',
    DELETE: 'border-rose-400/40 text-rose-300 bg-rose-500/5',
  };
  return (
    <div className="flex items-start gap-3 p-3 rounded-lg bg-black/30 border border-white/5 hover:border-white/20 transition">
      <Badge className={`${colors[method] || 'border-white/20'} uppercase font-mono text-[10px] w-14 justify-center shrink-0`}>{method}</Badge>
      <div className="min-w-0 flex-1">
        <code className="text-sm text-white font-mono break-all">{path}</code>
        {desc && <p className="text-[11px] text-white/60 mt-0.5">{desc}</p>}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Secciones de la doc
// ─────────────────────────────────────────────────────────────────────────

const SECTIONS = [
  { id: 'intro', label: 'Introducción', icon: BookOpen },
  { id: 'auth', label: 'Autenticación', icon: ShieldCheck },
  { id: 'leads', label: 'Leads', icon: Zap },
  { id: 'ai-summary', label: 'Resumen IA', icon: MessageSquare },
  { id: 'activities', label: 'Actividades', icon: MessageSquare },
  { id: 'properties', label: 'Propiedades', icon: Database },
  { id: 'opportunities', label: 'Oportunidades', icon: Zap },
  { id: 'pipeline', label: 'Pipeline & Team', icon: MessageSquare },
  { id: 'custom', label: 'Entidades personalizadas', icon: Code },
  { id: 'utm', label: 'UTM Tracking', icon: Link2 },
  { id: 'errors', label: 'Errores', icon: ShieldCheck },
];

const BASE_URL = 'https://api.dealider.com';

export default function ApiDocsPage() {
  const [active, setActive] = useState('intro');

  const scrollTo = (id) => {
    setActive(id);
    const el = document.getElementById(`section-${id}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const examples = useMemo(() => ({
    curlAuth: `curl -X GET '${BASE_URL}/api/v1/leads' \\
  -H 'X-Api-Key: dldr_TU_CLAVE_AQUI'`,
    curlCreateLead: `curl -X POST '${BASE_URL}/api/v1/leads' \\
  -H 'X-Api-Key: dldr_TU_CLAVE_AQUI' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "name": "Ana Pérez",
    "email": "ana@ejemplo.com",
    "phone": "+5215512345678",
    "source": "facebook_ads",
    "notes": "Interesada en casa 3 recámaras CDMX"
  }'`,
    nodeCreateLead: `// Node.js (axios)
import axios from 'axios';

const dealider = axios.create({
  baseURL: '${BASE_URL}/api/v1',
  headers: { 'X-Api-Key': process.env.DEALIDER_KEY }
});

const { data } = await dealider.post('/leads', {
  name: 'Ana Pérez',
  email: 'ana@ejemplo.com',
  phone: '+5215512345678',
  source: 'facebook_ads'
});
console.log('Lead creado:', data.id);`,
    pyCreateLead: `# Python (requests)
import os, requests

DEALIDER = "${BASE_URL}/api/v1"
HEADERS = {"X-Api-Key": os.environ["DEALIDER_KEY"]}

r = requests.post(f"{DEALIDER}/leads", json={
    "name": "Ana Pérez",
    "email": "ana@ejemplo.com",
    "phone": "+5215512345678",
    "source": "facebook_ads",
}, headers=HEADERS, timeout=15)
r.raise_for_status()
print("Lead creado:", r.json()["id"])`,
    curlCustomList: `curl -X GET '${BASE_URL}/api/custom/visitas' \\
  -H 'Authorization: Bearer TU_JWT'`,
    curlCustomCreate: `curl -X POST '${BASE_URL}/api/custom/visitas' \\
  -H 'Authorization: Bearer TU_JWT' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "cliente": "Carlos M.",
    "fecha": "2026-02-15",
    "comentario": "Le gustó la cocina"
  }'`,
    utmPreview: `curl -X POST '${BASE_URL}/api/utm-links/preview' \\
  -H 'Authorization: Bearer TU_JWT' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "label": "Email mayo",
    "base_url": "https://miempresa.com/landing",
    "utm_source": "newsletter",
    "utm_medium": "email",
    "utm_campaign": "lanzamiento-mayo"
  }'`,
    curlGetByPhone: `curl -X GET '${BASE_URL}/api/v1/leads/by-phone/5215512345678' \\
  -H 'X-Api-Key: dldr_TU_CLAVE_AQUI'`,
    curlLeadsByVendor: `curl -X GET '${BASE_URL}/api/v1/leads/by-vendor/5217777045297' \\
  -H 'X-Api-Key: dldr_TU_CLAVE_AQUI'`,
    curlCreateActivity: `curl -X POST '${BASE_URL}/api/v1/activities' \\
  -H 'X-Api-Key: dldr_TU_CLAVE_AQUI' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "type": "note",
    "description": "Cliente pidió cotización por WhatsApp",
    "lead_id": "f23a08fc-a1a9-4069-8083-f171480ce361"
  }'`,
    curlPatchOppStage: `curl -X PATCH '${BASE_URL}/api/v1/opportunities/by-phone/5215512345678' \\
  -H 'X-Api-Key: dldr_TU_CLAVE_AQUI' \\
  -H 'Content-Type: application/json' \\
  -d '{ "stage": "Cita agendada" }'`,
  }), []);

  const logoUrl = BRAND.logoWhite;

  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="api-docs-page">
      {/* Header */}
      <header className="sticky top-0 z-20 backdrop-blur-md bg-slate-950/80 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="inline-flex items-center gap-2 text-white/60 hover:text-white text-xs" data-testid="docs-back-home">
              <ArrowLeft className="w-3.5 h-3.5" /> Volver
            </Link>
            <Link to="/" className="flex items-center gap-2">
              <img src={logoUrl} alt="LEADCEM" className="h-7" />
              <span className="text-[11px] uppercase tracking-widest text-white/40 border-l border-white/10 pl-3">REST API · v1</span>
            </Link>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="border-emerald-400/40 text-emerald-300 text-[10px]">v1 · Stable</Badge>
            <Link to="/login">
              <Button size="sm" className="bg-violet-500 hover:bg-violet-500/90 text-white text-xs h-8" data-testid="docs-get-key-btn">
                Obtener API Key
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-8">
        {/* Sidebar */}
        <aside className="lg:sticky lg:top-20 self-start space-y-1">
          <p className="text-[10px] uppercase tracking-widest text-white/40 mb-2 px-2">Secciones</p>
          {SECTIONS.map(s => {
            const Icon = s.icon;
            return (
              <button
                key={s.id}
                onClick={() => scrollTo(s.id)}
                className={`w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg text-xs transition ${
                  active === s.id
                    ? 'bg-violet-500/15 text-violet-200 border border-violet-400/30'
                    : 'text-white/60 hover:text-white hover:bg-white/5'
                }`}
                data-testid={`docs-nav-${s.id}`}
              >
                <Icon className="w-3.5 h-3.5" /> {s.label}
                {active === s.id && <ChevronRight className="w-3 h-3 ml-auto" />}
              </button>
            );
          })}
          <div className="mt-6 p-3 rounded-lg bg-violet-500/5 border border-violet-400/20">
            <p className="text-[11px] text-violet-200 font-semibold mb-1">¿Necesitas ayuda?</p>
            <p className="text-[10px] text-white/60 leading-snug">Contáctanos para soporte técnico de integración.</p>
            <a href="mailto:soporte@dealider.com" className="text-[10px] text-cyan-300 hover:underline mt-1 inline-block">soporte@dealider.com</a>
          </div>
        </aside>

        {/* Content */}
        <main className="space-y-14 pb-20">
          {/* INTRO */}
          <section id="section-intro">
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-2">{APP_NAME} REST API</h1>
            <p className="text-white/70 text-sm leading-relaxed max-w-3xl">
              La API REST de {APP_NAME} te permite integrar tu CRM con cualquier sistema externo:
              automatizadores (<span className="text-violet-300">Zapier, n8n, Make</span>), sitios web propios,
              scripts internos, formularios de Facebook Lead Ads, o tu propia app móvil.
            </p>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-3xl">
              <Pill title="Base URL" value={BASE_URL} />
              <Pill title="Versión" value="v1" />
              <Pill title="Formato" value="JSON" />
            </div>
            <div className="mt-6 p-4 rounded-lg bg-cyan-500/5 border border-cyan-400/20 max-w-3xl">
              <p className="text-xs text-cyan-200 font-semibold mb-1">Antes de empezar</p>
              <ol className="text-xs text-white/70 space-y-1 list-decimal list-inside">
                <li>Crea una cuenta en <Link to="/login" className="text-cyan-300 underline">app.dealider.com</Link></li>
                <li>Ve a <span className="text-white">API Hub → API Keys</span> y genera tu clave</li>
                <li>Cópiala (solo se muestra una vez) y guárdala en un gestor seguro</li>
              </ol>
            </div>
          </section>

          {/* AUTH */}
          <section id="section-auth">
            <h2 className="font-heading text-2xl font-bold text-white mb-3">Autenticación</h2>
            <p className="text-sm text-white/70 mb-4 max-w-3xl">
              Todos los endpoints públicos (prefijo <code className="text-violet-300">/api/v1/</code>) requieren el header
              <code className="text-violet-300"> X-Api-Key</code> con una clave válida de tu empresa.
            </p>
            <CodeBlock code={examples.curlAuth} />
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-3xl">
              <Pill title="Header" value="X-Api-Key" mono />
              <Pill title="Formato" value="dldr_..." mono />
              <Pill title="Scope" value="por empresa" />
            </div>
            <p className="text-[11px] text-white/50 mt-4 max-w-3xl">
              ⚠️ Nunca expongas tu API Key en código del lado del cliente. Llámala siempre desde tu backend o un proxy seguro.
            </p>
          </section>

          {/* LEADS */}
          <section id="section-leads">
            <h2 className="font-heading text-2xl font-bold text-white mb-3">Leads</h2>
            <p className="text-sm text-white/70 mb-4 max-w-3xl">
              Crea y consulta leads. Al crear un lead, automáticamente se asigna por <span className="text-cyan-300">ruleta</span> o reglas (según tu config) y dispara las automatizaciones de IA.
            </p>
            <div className="space-y-2 max-w-3xl mb-4">
              <EndpointBox method="GET" path="/api/v1/leads" desc="Lista hasta 500 leads de tu empresa, más recientes primero." />
              <EndpointBox method="POST" path="/api/v1/leads" desc="Crea un lead. Lo asigna automáticamente vía ruleta/reglas." />
              <EndpointBox method="GET" path="/api/v1/leads/by-phone/{telefono}" desc="Consulta de SOLO LECTURA: devuelve id, status y notes del lead por teléfono, sin modificar nada. 404 si no existe en tu empresa." />
              <EndpointBox method="PATCH" path="/api/v1/leads/by-phone/{telefono}" desc="Actualiza status y/o agrega notes (append) del lead por teléfono. Requiere al menos uno de los dos." />
              <EndpointBox method="GET" path="/api/v1/leads/by-vendor/{telefono}" desc="SOLO LECTURA: hasta 10 leads asignados a un vendedor (identificado por su teléfono de WhatsApp), más recientes primero. Excluye 'perdido' por default; ?status= filtra a un status exacto. 404 si el vendedor no existe." />
            </div>
            <p className="text-xs text-white/60 mb-2 mt-4">Ejemplo — consultar lead por teléfono (solo lectura):</p>
            <CodeBlock code={examples.curlGetByPhone} />
            <p className="text-xs text-white/60 mb-2 mt-4">Ejemplo — leads asignados a un vendedor (solo lectura):</p>
            <CodeBlock code={examples.curlLeadsByVendor} />
            <p className="text-xs text-white/60 mb-2">Ejemplo — crear lead con cURL:</p>
            <CodeBlock code={examples.curlCreateLead} />
            <p className="text-xs text-white/60 mb-2 mt-4">Con Node.js:</p>
            <CodeBlock code={examples.nodeCreateLead} lang="javascript" />
            <p className="text-xs text-white/60 mb-2 mt-4">Con Python:</p>
            <CodeBlock code={examples.pyCreateLead} lang="python" />
          </section>

          {/* AI SUMMARY */}
          <section id="section-ai-summary">
            <h2 className="font-heading text-2xl font-bold text-white mb-3">Resumen IA del lead</h2>
            <p className="text-sm text-white/70 mb-4 max-w-3xl">
              Devuelve un resumen en lenguaje natural de la conversación del lead con el agente de IA,
              pensado para darle <span className="text-cyan-300">contexto rápido al vendedor</span>:
              qué busca, presupuesto o condiciones, en qué quedó la charla y el nivel de interés.
              El resumen se genera bajo demanda y se <span className="text-cyan-300">cachea</span>:
              si la conversación no cambió, no se vuelve a generar.
            </p>
            <div className="space-y-2 max-w-3xl mb-4">
              <EndpointBox method="GET" path="/api/v1/leads/{lead_id}/ai-summary" desc="Resumen de la conversación IA del lead. Si el lead aún no ha conversado, devuelve has_conversation:false (200). 404 si el lead no existe en tu empresa." />
            </div>
            <CodeBlock code={`curl -X GET '${BASE_URL}/api/v1/leads/LEAD_ID/ai-summary' \\
  -H 'X-Api-Key: TU_API_KEY'`} />
          </section>

          {/* ACTIVITIES */}
          <section id="section-activities">
            <h2 className="font-heading text-2xl font-bold text-white mb-3">Actividades</h2>
            <p className="text-sm text-white/70 mb-4 max-w-3xl">
              Registra una <span className="text-cyan-300">nota</span> sobre un lead desde un sistema externo
              (ej. un Flow de WhatsApp) sin entrar a la plataforma. La nota queda en la bitácora del lead
              y se marca con origen <code className="text-violet-300">api_integration</code>, así que
              <span className="text-cyan-300"> no se cuenta como proactividad humana</span> en los reportes.
            </p>
            <div className="space-y-2 max-w-3xl mb-4">
              <EndpointBox method="POST" path="/api/v1/activities" desc="Registra una nota (type=note) sobre un lead. Requiere lead_id (el campo id que devuelve el PATCH de leads). 404 si el lead no existe en tu empresa." />
            </div>
            <p className="text-[11px] text-amber-200 mb-4 max-w-3xl">
              Por ahora solo se admite <code>type: "note"</code>. El <code>lead_id</code> es el mismo
              <code> id</code> que regresa <code>PATCH /api/v1/leads/by-phone/&#123;telefono&#125;</code>.
            </p>
            <CodeBlock code={examples.curlCreateActivity} />
          </section>

          {/* PROPERTIES */}
          <section id="section-properties">
            <h2 className="font-heading text-2xl font-bold text-white mb-3">Propiedades</h2>
            <p className="text-sm text-white/70 mb-4 max-w-3xl">
              Listado y creación de propiedades inmobiliarias del catálogo de tu empresa.
            </p>
            <div className="space-y-2 max-w-3xl">
              <EndpointBox method="GET" path="/api/v1/properties" desc="Devuelve tus propiedades activas, hasta 500." />
              <EndpointBox method="POST" path="/api/v1/properties" desc="Crea una propiedad nueva en tu catálogo." />
            </div>
          </section>

          {/* OPPORTUNITIES */}
          <section id="section-opportunities">
            <h2 className="font-heading text-2xl font-bold text-white mb-3">Oportunidades</h2>
            <p className="text-sm text-white/70 mb-4 max-w-3xl">
              Operaciones en el pipeline. Útil para sincronizar tu CRM con sistemas de cotización o post-venta externos.
            </p>
            <div className="space-y-2 max-w-3xl mb-4">
              <EndpointBox method="GET" path="/api/v1/opportunities" desc="Lista de oportunidades de tu empresa." />
              <EndpointBox method="POST" path="/api/v1/opportunities" desc="Crea una oportunidad nueva (se asigna al stage inicial si no especificas otro)." />
              <EndpointBox method="PATCH" path="/api/v1/opportunities/by-phone/{telefono}" desc="Cambia SOLO la etapa (stage) de la oportunidad del lead, buscándolo por teléfono. No toca ningún otro campo. Opera sobre la oportunidad más reciente del lead." />
            </div>
            <p className="text-[11px] text-amber-200 mb-4 max-w-3xl">
              El campo <code>stage</code> acepta el <span className="text-cyan-300">nombre visible</span> de la etapa
              (ej. <code>"Cita agendada"</code>); se resuelve sin distinguir acentos ni mayúsculas. Si el nombre no
              existe, el error <code>400</code> devuelve la lista de etapas válidas de tu empresa.
            </p>
            <CodeBlock code={examples.curlPatchOppStage} />
          </section>

          {/* PIPELINE */}
          <section id="section-pipeline">
            <h2 className="font-heading text-2xl font-bold text-white mb-3">Pipeline & Team</h2>
            <p className="text-sm text-white/70 mb-4 max-w-3xl">
              Lee la configuración de etapas y los miembros del equipo (útil para clasificar leads o asignar manualmente desde tu backend).
            </p>
            <div className="space-y-2 max-w-3xl">
              <EndpointBox method="GET" path="/api/v1/pipeline" desc="Etapas del pipeline configuradas en tu cuenta." />
              <EndpointBox method="GET" path="/api/v1/team" desc="Miembros del equipo (sin passwords, obviamente)." />
            </div>
          </section>

          {/* CUSTOM */}
          <section id="section-custom">
            <h2 className="font-heading text-2xl font-bold text-white mb-3">Entidades personalizadas</h2>
            <p className="text-sm text-white/70 mb-4 max-w-3xl">
              Crea entidades CRUD propias sin programar. Defínelas en <span className="text-cyan-300">API Hub → Generador de APIs</span> y obtén endpoints automáticos en <code className="text-violet-300">/api/custom/&#123;slug&#125;</code>.
            </p>
            <p className="text-[11px] text-amber-200 mb-4 max-w-3xl">
              Las entidades custom usan el JWT de sesión (header <code>Authorization: Bearer ...</code>), no la API Key v1.
            </p>
            <div className="space-y-2 max-w-3xl mb-4">
              <EndpointBox method="GET" path="/api/custom/{slug}" desc="Lista items de tu entidad personalizada." />
              <EndpointBox method="POST" path="/api/custom/{slug}" desc="Crea un item nuevo respetando el esquema definido." />
              <EndpointBox method="GET" path="/api/custom/{slug}/{id}" desc="Obtiene un item específico." />
              <EndpointBox method="PUT" path="/api/custom/{slug}/{id}" desc="Actualiza un item." />
              <EndpointBox method="DELETE" path="/api/custom/{slug}/{id}" desc="Elimina un item." />
            </div>
            <CodeBlock code={examples.curlCustomList} />
            <div className="h-3" />
            <CodeBlock code={examples.curlCustomCreate} />
          </section>

          {/* UTM */}
          <section id="section-utm">
            <h2 className="font-heading text-2xl font-bold text-white mb-3">UTM Tracking</h2>
            <p className="text-sm text-white/70 mb-4 max-w-3xl">
              Genera URLs con parámetros UTM correctamente codificados desde tu backend (útil para enviar miles en campañas masivas sin errores de encoding).
            </p>
            <div className="space-y-2 max-w-3xl mb-4">
              <EndpointBox method="POST" path="/api/utm-links/preview" desc="Devuelve la URL final sin guardar nada. Ideal para mostrar preview en UI." />
              <EndpointBox method="POST" path="/api/utm-links" desc="Genera la URL y la guarda en el historial del equipo." />
              <EndpointBox method="GET" path="/api/utm-links" desc="Lista los UTMs guardados por la empresa." />
              <EndpointBox method="DELETE" path="/api/utm-links/{id}" desc="Elimina un link del historial." />
            </div>
            <CodeBlock code={examples.utmPreview} />
          </section>

          {/* ERRORS */}
          <section id="section-errors">
            <h2 className="font-heading text-2xl font-bold text-white mb-3">Errores</h2>
            <p className="text-sm text-white/70 mb-4 max-w-3xl">
              Los errores siguen el formato estándar HTTP. El cuerpo incluye <code className="text-violet-300">&#123; "detail": "mensaje" &#125;</code>.
            </p>
            <div className="space-y-2 max-w-3xl">
              {[
                ['400', 'Solicitud inválida (payload incorrecto o campos faltantes).'],
                ['401', 'API Key faltante o inválida.'],
                ['403', 'Tu rol no permite esta acción (ej. crear keys requiere admin).'],
                ['404', 'Recurso no encontrado.'],
                ['429', 'Demasiadas solicitudes (rate limit alcanzado).'],
                ['500', 'Error interno — contacta a soporte si persiste.'],
              ].map(([code, desc]) => (
                <div key={code} className="flex items-start gap-3 p-3 rounded-lg bg-black/30 border border-white/5">
                  <Badge variant="outline" className="border-rose-400/40 text-rose-300 font-mono text-xs w-12 justify-center shrink-0">{code}</Badge>
                  <p className="text-xs text-white/70">{desc}</p>
                </div>
              ))}
            </div>
            <div className="mt-8 p-4 rounded-lg bg-white/[0.02] border border-white/10 max-w-3xl">
              <p className="text-xs text-white/60">
                ¿Lista para integrar? <Link to="/login" className="text-violet-300 underline">Inicia sesión</Link> y genera tu primera API Key en el API Hub.
              </p>
            </div>
          </section>
        </main>
      </div>

      {/* Footer */}
      <footer className="border-t border-white/5 py-6 text-center text-[11px] text-white/40">
        © {new Date().getFullYear()} {APP_NAME} · <Link to="/terminos" className="hover:text-white/60">Términos</Link> · <Link to="/privacidad" className="hover:text-white/60">Privacidad</Link>
      </footer>
    </div>
  );
}

function Pill({ title, value, mono }) {
  return (
    <div className="p-3 rounded-lg bg-black/30 border border-white/5">
      <p className="text-[9px] uppercase tracking-widest text-white/40">{title}</p>
      <p className={`text-xs text-white ${mono ? 'font-mono text-violet-200' : ''} mt-1`}>{value}</p>
    </div>
  );
}
